import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Rocket, Zap, Plus, Edit, Trash2, TrendingUp } from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL;

export default function SelfMarketing() {
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [settings, setSettings] = useState(null);
  const [ctas, setCtas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  // CTA form state
  const [newCta, setNewCta] = useState({
    text: '',
    url: '',
    category: 'primary',
    priority: 0
  });

  // Load brands on mount
  useEffect(() => {
    loadBrands();
  }, []);

  // Load data when brand is selected
  useEffect(() => {
    if (selectedBrand) {
      loadSettings();
      loadCtas();
      loadStats();
    }
  }, [selectedBrand]);

  const loadBrands = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/brands`, {
        credentials: 'include'
      });
      const data = await res.json();
      setBrands(data);
      if (data.length > 0) {
        setSelectedBrand(data[0].id);
      }
    } catch (error) {
      console.error('Error loading brands:', error);
    }
  };

  const loadSettings = async () => {
    if (!selectedBrand) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/self-marketing/settings/${selectedBrand}`, {
        credentials: 'include'
      });
      const data = await res.json();
      setSettings(data);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadCtas = async () => {
    if (!selectedBrand) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/self-marketing/ctas/${selectedBrand}`, {
        credentials: 'include'
      });
      const data = await res.json();
      setCtas(data);
    } catch (error) {
      console.error('Error loading CTAs:', error);
    }
  };

  const loadStats = async () => {
    if (!selectedBrand) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/self-marketing/stats/${selectedBrand}`, {
        credentials: 'include'
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const updateSettings = async (updates) => {
    if (!selectedBrand) return;
    setLoading(true);
    try {
      const params = new URLSearchParams(updates);
      const res = await fetch(`${BACKEND_URL}/api/self-marketing/settings/${selectedBrand}?${params}`, {
        method: 'PUT',
        credentials: 'include'
      });
      const data = await res.json();
      setMessage({ type: 'success', text: 'Settings updated successfully!' });
      loadSettings();
      loadStats();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update settings' });
      console.error('Error updating settings:', error);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const toggleMode = async (mode) => {
    if (!selectedBrand) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/self-marketing/settings/${selectedBrand}/toggle-mode?mode=${mode}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      setMessage({ type: 'success', text: `Switched to ${mode.toUpperCase()} mode!` });
      loadSettings();
      loadStats();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to toggle mode' });
      console.error('Error toggling mode:', error);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const createCta = async () => {
    if (!selectedBrand || !newCta.text || !newCta.url) return;
    setLoading(true);
    try {
      const params = new URLSearchParams(newCta);
      const res = await fetch(`${BACKEND_URL}/api/self-marketing/ctas/${selectedBrand}?${params}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      setMessage({ type: 'success', text: 'CTA created successfully!' });
      setNewCta({ text: '', url: '', category: 'primary', priority: 0 });
      loadCtas();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to create CTA' });
      console.error('Error creating CTA:', error);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const deleteCta = async (ctaId) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/self-marketing/ctas/${ctaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await res.json();
      setMessage({ type: 'success', text: 'CTA deleted successfully!' });
      loadCtas();
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete CTA' });
      console.error('Error deleting CTA:', error);
    } finally {
      setLoading(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  if (brands.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>No Brands Found</CardTitle>
            <CardDescription>Create a brand first to use Self-Marketing</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold mb-2">Self-Marketing Engine</h1>
        <p className="text-muted-foreground">Admin-only control panel for automated marketing campaigns</p>
      </div>

      {message && (
        <Alert className={`mb-4 ${message.type === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
          <AlertDescription>{message.text}</AlertDescription>
        </Alert>
      )}

      {/* Brand Selector */}
      <div className="mb-6">
        <Label>Select Brand</Label>
        <select
          className="w-full mt-2 p-2 border rounded"
          value={selectedBrand || ''}
          onChange={(e) => setSelectedBrand(e.target.value)}
        >
          {brands.map(brand => (
            <option key={brand.id} value={brand.id}>{brand.name}</option>
          ))}
        </select>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="ctas">CTAs</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Stats Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5" />
                  Statistics
                </CardTitle>
              </CardHeader>
              <CardContent>
                {stats && (
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-muted-foreground">Status</div>
                      <div className="font-semibold">
                        {stats.enabled ? (
                          <Badge className="bg-green-500">Enabled</Badge>
                        ) : (
                          <Badge variant="outline">Disabled</Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Current Mode</div>
                      <div className="font-semibold text-lg">
                        {stats.mode === 'blitz' ? (
                          <span className="text-orange-500 flex items-center gap-2">
                            <Zap className="h-4 w-4" /> BLITZ
                          </span>
                        ) : (
                          <span className="text-blue-500 flex items-center gap-2">
                            <Rocket className="h-4 w-4" /> NORMAL
                          </span>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Posts Per Day</div>
                      <div className="font-semibold text-2xl">{stats.posts_per_day}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-muted-foreground">Scheduled</div>
                        <div className="font-semibold text-xl">{stats.posts_scheduled}</div>
                      </div>
                      <div>
                        <div className="text-sm text-muted-foreground">Posted</div>
                        <div className="font-semibold text-xl">{stats.posts_posted}</div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions Card */}
            <Card>
              <CardHeader>
                <CardTitle>Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {settings && (
                  <>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold">Self-Marketing</div>
                        <div className="text-sm text-muted-foreground">Enable/disable engine</div>
                      </div>
                      <Switch
                        checked={settings.is_self_marketing_enabled}
                        onCheckedChange={(checked) => updateSettings({ is_self_marketing_enabled: checked })}
                        disabled={loading}
                      />
                    </div>

                    <div className="border-t pt-4">
                      <div className="font-semibold mb-3">Mode Switch</div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant={settings.self_marketing_mode === 'normal' ? 'default' : 'outline'}
                          onClick={() => toggleMode('normal')}
                          disabled={loading}
                          className="w-full"
                        >
                          <Rocket className="h-4 w-4 mr-2" />
                          Normal
                        </Button>
                        <Button
                          variant={settings.self_marketing_mode === 'blitz' ? 'default' : 'outline'}
                          onClick={() => toggleMode('blitz')}
                          disabled={loading}
                          className="w-full"
                        >
                          <Zap className="h-4 w-4 mr-2" />
                          Blitz
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Engine Configuration</CardTitle>
              <CardDescription>Adjust parameters for Normal and Blitz modes</CardDescription>
            </CardHeader>
            <CardContent>
              {settings && (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>Normal Posts Per Day (4-6)</Label>
                      <Input
                        type="number"
                        min="4"
                        max="6"
                        value={settings.normal_posts_per_day}
                        onChange={(e) => setSettings({...settings, normal_posts_per_day: parseInt(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Blitz Posts Per Day (8-12)</Label>
                      <Input
                        type="number"
                        min="8"
                        max="12"
                        value={settings.blitz_posts_per_day}
                        onChange={(e) => setSettings({...settings, blitz_posts_per_day: parseInt(e.target.value)})}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label>Clip Multiplier (1-3)</Label>
                      <Input
                        type="number"
                        min="1"
                        max="3"
                        value={settings.clip_multiplier}
                        onChange={(e) => setSettings({...settings, clip_multiplier: parseInt(e.target.value)})}
                      />
                    </div>
                    <div>
                      <Label>Schedule Tightening (0-1)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={settings.schedule_tightening_factor}
                        onChange={(e) => setSettings({...settings, schedule_tightening_factor: parseFloat(e.target.value)})}
                      />
                    </div>
                  </div>

                  <div>
                    <Label>CTA Rotation Speed</Label>
                    <select
                      className="w-full mt-2 p-2 border rounded"
                      value={settings.cta_rotation_speed}
                      onChange={(e) => setSettings({...settings, cta_rotation_speed: e.target.value})}
                    >
                      <option value="normal">Normal</option>
                      <option value="accelerated">Accelerated</option>
                    </select>
                  </div>

                  <Button
                    onClick={() => updateSettings({
                      normal_posts_per_day: settings.normal_posts_per_day,
                      blitz_posts_per_day: settings.blitz_posts_per_day,
                      clip_multiplier: settings.clip_multiplier,
                      schedule_tightening_factor: settings.schedule_tightening_factor,
                      cta_rotation_speed: settings.cta_rotation_speed
                    })}
                    disabled={loading}
                    className="w-full"
                  >
                    Save Settings
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CTAs Tab */}
        <TabsContent value="ctas">
          <div className="grid gap-6">
            {/* Create CTA Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Plus className="h-5 w-5" />
                  Create New CTA
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Text</Label>
                    <Input
                      placeholder="e.g., Join Waitlist"
                      value={newCta.text}
                      onChange={(e) => setNewCta({...newCta, text: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>URL</Label>
                    <Input
                      placeholder="e.g., /waitlist"
                      value={newCta.url}
                      onChange={(e) => setNewCta({...newCta, url: e.target.value})}
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label>Category</Label>
                    <select
                      className="w-full mt-2 p-2 border rounded"
                      value={newCta.category}
                      onChange={(e) => setNewCta({...newCta, category: e.target.value})}
                    >
                      <option value="primary">Primary</option>
                      <option value="secondary">Secondary</option>
                      <option value="urgency">Urgency</option>
                    </select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Input
                      type="number"
                      min="0"
                      value={newCta.priority}
                      onChange={(e) => setNewCta({...newCta, priority: parseInt(e.target.value)})}
                    />
                  </div>
                </div>
                <Button onClick={createCta} disabled={loading || !newCta.text || !newCta.url}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create CTA
                </Button>
              </CardContent>
            </Card>

            {/* CTAs List */}
            <Card>
              <CardHeader>
                <CardTitle>Active CTAs</CardTitle>
                <CardDescription>{ctas.length} CTAs configured</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {ctas.map(cta => (
                    <div key={cta.id} className="flex items-center justify-between p-4 border rounded">
                      <div className="flex-1">
                        <div className="font-semibold">{cta.text}</div>
                        <div className="text-sm text-muted-foreground">{cta.url}</div>
                        <div className="flex gap-2 mt-2">
                          <Badge variant="outline">{cta.category}</Badge>
                          <Badge variant="outline">Priority: {cta.priority}</Badge>
                          <Badge variant="outline">Uses: {cta.usage_count}</Badge>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => deleteCta(cta.id)}
                        disabled={loading}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  {ctas.length === 0 && (
                    <div className="text-center text-muted-foreground py-8">
                      No CTAs created yet. Create your first CTA above.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
